import { cnMerge, createTV, type ClassValue } from 'tailwind-variants';

/**
 * tailwind-merge must know the custom design scales (spec 01-design-system §8.6), otherwise conflicting
 * classes such as `rounded-8` + `rounded-12` or `shadow-xs` + `shadow-knob` are not de-duplicated and the
 * stylesheet order decides. Every `tv()` in shared/ui uses this config, so `component({ class: className })`
 * lets callers override any design class safely.
 *
 * Gotcha (tailwind-merge v3): a font-size class removes an *earlier* `leading-*` class (`'font-size': ['leading']`).
 * Always write the size first and the line-height after it: `text-13 leading-20`, never `leading-20 text-13`.
 */
export const twMergeConfig = {
  extend: {
    theme: {
      text: ['xxs', '9', '10', '11', '13', '15', '26', '28'],
      radius: ['0', '1', '2', '3', '4', '6', '7', '8', '9', '10', '12', '14', '16', '18', '20', '24', '30', '32', '40', '48', '53'],
      shadow: ['xs', 'knob', 'toast', 'float', 'dropdown', 'pin', 'popover', 'focus', 'focus-field', 'glow-success'],
      blur: ['label', 'pill', 'glass', 'dropdown', 'card', 'card-lg', 'haze', 'glow'],
      leading: ['auto', 'figma-120'],
      tracking: ['figma', 'caps', 'caps-wide'],
    },
  },
};

export const tv = createTV({ twMergeConfig });

/**
 * `cn()` with the design-scale merge config (tailwind-variants' merger). Used for root `className` merges of
 * shared/ui components so caller overrides such as `rounded-8` or `shadow-float` reliably win.
 * (`@/shared/lib/cn` only knows the custom text sizes — spec §8.6 asks for the full config there too.)
 */
export const cx = (...classes: ClassValue[]): string => cnMerge(...classes)({ twMergeConfig }) ?? '';

export type { VariantProps } from 'tailwind-variants';
